import React from 'react';
import 'katex/dist/katex.min.css';
var Latex = require('../utils/SafeLatex').default;

/**
 * LatexRenderer with optional TTS sentence wrapping.
 *
 * Props:
 *   children: string — raw text with LaTeX
 *   ttsSegments: Array<{ globalIndex, type }> — if provided, wraps output in sentence spans
 *   ttsStartIndex: number — the globalIndex of the first segment that maps to this text block
 *   ttsActiveIndex: number|null — currently speaking segment index (for highlighting)
 *   ttsClickMode: boolean — whether click-to-speak is active
 *   onTTSClick: (globalIndex) => void — called when a TTS sentence span is clicked
 */
const LatexRenderer = ({
  children: raw,
  ttsSegments,
  ttsStartIndex = 0,
  ttsActiveIndex = null,
  ttsClickMode = false,
  onTTSClick,
}) => {
  const children = typeof raw === 'string' ? mathCurrency(raw) : raw;
  const processText = (text) => {
    if (!text) return text;
    if (typeof text === 'string') text = sizeFractions(text);

    // Find all instances of $\underline{\text{...}}$ and extract them
    const underlinePattern = /\$\\underline\{\\text\{([^}]+)\}\}\$/g;
    const matches = [];
    let match;

    while ((match = underlinePattern.exec(text)) !== null) {
      matches.push({
        fullMatch: match[0],
        textContent: match[1],
        index: match.index
      });
    }

    if (matches.length === 0) {
      return <Latex>{text}</Latex>;
    }

    const segments = [];
    let lastIndex = 0;

    matches.forEach((match, i) => {
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index);
        if (beforeText) {
          segments.push(<Latex key={`latex-${i}`}>{beforeText}</Latex>);
        }
      }
      segments.push(
        <span key={`underline-${i}`} className="latex-underline">
          {match.textContent}
        </span>
      );
      lastIndex = match.index + match.fullMatch.length;
    });

    if (lastIndex < text.length) {
      const afterText = text.substring(lastIndex);
      if (afterText) {
        segments.push(<Latex key="latex-final">{afterText}</Latex>);
      }
    }

    return segments;
  };

  // If no TTS segments, render normally
  if (!ttsSegments || ttsSegments.length === 0) {
    // KaTeX draws each $\newline$ as an empty block, so two in a row collapse
    // into one break and an intended blank line (e.g. between "The following
    // text is from..." and the excerpt) disappeared. Turn the markers into real
    // breaks first, like the TTS path below: one marker = new line, two or more
    // = blank line. This also keeps $\newline$ out of KaTeX, which the
    // inline-block .katex rule in LatexRenderer.css relies on.
    // Note bullets ($\qquad$$\bullet$ ...) get a hanging indent, as in the TTS
    // path, so wrapped lines line up under the text, not back at the margin.
    if (typeof children === 'string' && /\$\$?\\newline\$\$?/.test(children)) {
      const pieces = children.split(/((?:\s*\$\$?\\newline\$\$?)+\s*)/);
      const BULLET = /^\s*(?:\$\\qquad\$\s*)?\$\\bullet\$\s*/;
      const out = [];
      let prevBlock = false;
      for (let i = 0; i < pieces.length; i += 2) {
        const line = pieces[i];
        if (!line.trim()) continue;
        const isBullet = BULLET.test(line);
        if (out.length) {
          const blank = (pieces[i - 1].match(/\\newline/g) || []).length >= 2;
          // A block starts its own line, and after a block one <br> already
          // makes a blank line, so count breaks around bullets differently.
          const breaks = isBullet ? 0 : prevBlock ? (blank ? 1 : 0) : (blank ? 2 : 1);
          for (let b = 0; b < breaks; b++) out.push(<br key={`br-${i}-${b}`} />);
        }
        out.push(isBullet
          ? <span key={`line-${i}`} style={{ display: 'block', paddingLeft: '3.5em', textIndent: '-1em' }}>{processText(`● ${line.replace(BULLET, '')}`)}</span>
          : <React.Fragment key={`line-${i}`}>{processText(line)}</React.Fragment>);
        prevBlock = isBullet;
      }
      return <div className="latex-renderer-container">{out}</div>;
    }
    return (
      <div className="latex-renderer-container">
        {processText(children)}
      </div>
    );
  }

  // TTS mode: split the raw text into sentence chunks matching ttsSegments,
  // then wrap each in a span with data-tts-index.
  // We use the same splitting logic as buildTTSSegments to stay in sync.
  const sentenceChunks = splitRawIntoChunks(children);

  return (
    <div className="latex-renderer-container">
      {sentenceChunks.map((chunk, i) => {
        const gi = ttsStartIndex + i;
        const isActive = ttsActiveIndex === gi;
        const isBullet = chunk.text.trimStart().startsWith('•');
        const breaks = isBullet ? 0 : chunk.breakCount;
        const bulletText = isBullet ? chunk.text.replace('•', '●') : chunk.text;
        return (
          <React.Fragment key={gi}>
            {Array.from({ length: breaks }, (_, j) => <br key={`br-${gi}-${j}`} />)}
            <span
              data-tts-index={gi}
              className={isActive ? 'tts-active' : ''}
              onClick={ttsClickMode && onTTSClick ? (e) => { e.stopPropagation(); onTTSClick(gi); } : undefined}
              style={{ ...(ttsClickMode ? { cursor: 'pointer' } : {}), ...(isBullet ? { paddingLeft: '3.5em', textIndent: '-1em', display: 'block' } : {}) }}
            >
              {processText(bulletText)}{' '}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * A bare \$ in text (charges \$29 per month) renders as its own KaTeX box, an
 * inline-block, so a line could break between the $ and the amount ("charges $"
 * then "29 per month", 2026-10-18). Outside math, put the sign and the amount in
 * one math run, $\$29$, the form bank questions use and the read-aloud
 * splitter already keeps whole. Thousands commas become {,} so KaTeX adds no
 * space after them. Math runs, display math and a \$ with no amount are left
 * alone.
 */
export function mathCurrency(text) {
  if (!text.includes('\\$')) return text;
  let out = '';
  let inMath = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '\\' && text[i + 1] === '$') {
      const amount = !inMath && /^\d(?:\d|,\d{3})*(?:\.\d+)?/.exec(text.slice(i + 2));
      if (amount) {
        out += `$\\$${amount[0].replace(/,/g, '{,}')}$`;
        i += 1 + amount[0].length;
      } else {
        out += '\\$';
        i += 1;
      }
      continue;
    }
    if (c === '$') {
      if (text[i + 1] === '$') { out += '$$'; i += 1; inMath = !inMath; continue; }
      inMath = !inMath;
    }
    out += c;
  }
  return out;
}

/**
 * Inline \frac draws its numerator and denominator at 70% of the math size,
 * which read as tiny next to the question text (Sandy 2026-10-05); Bluebook
 * shows them full size. Make each \frac a \dfrac, except inside an exponent or
 * subscript, where a full-size fraction would tower over the line (KaTeX keeps
 * \tfrac there at 50% too, so LatexRenderer.css enlarges those instead).
 */
export function sizeFractions(tex) {
  if (!tex.includes('\\frac')) return tex;
  let out = '';
  const inScript = []; // one entry per open brace: is this group in a ^ or _?
  for (let i = 0; i < tex.length; i++) {
    const c = tex[i];
    if (c === '{') {
      const before = tex.slice(0, i).trimEnd();
      const last = before[before.length - 1];
      inScript.push(last === '^' || last === '_' || (inScript.length > 0 && inScript[inScript.length - 1]));
    } else if (c === '}') {
      inScript.pop();
    } else if (tex.startsWith('\\frac', i) && !/[a-zA-Z]/.test(tex[i + 5] || '')) {
      if (!(inScript.length && inScript[inScript.length - 1])) {
        out += '\\dfrac';
        i += 4;
        continue;
      }
    }
    out += c;
  }
  return out;
}

/**
 * Split raw LaTeX text into sentence chunks, matching the logic in buildTTSSegments.
 * This ensures the i-th chunk corresponds to the i-th TTS segment for this text block.
 */
// Emulates working.split(/((?<=[.!?])\s+|\n+)/) — segments at even indices,
// separators at odd — without regex lookbehind, which iOS Safari < 16.4 rejects
// at parse time (a lookbehind literal anywhere in the eager bundle white-screens
// the whole app on those devices).
function splitSentenceParts(s) {
  const parts = [];
  let last = 0;
  const emit = (start, end) => { parts.push(s.slice(last, start), s.slice(start, end)); last = end; };
  const ws = /\s+/g;
  let m;
  while ((m = ws.exec(s)) !== null) {
    const prev = m.index > 0 ? s[m.index - 1] : '';
    if (prev === '.' || prev === '!' || prev === '?') {
      // Whitespace run after sentence-ending punctuation: whole run separates.
      emit(m.index, m.index + m[0].length);
    } else {
      // Otherwise only the newline part of this whitespace run separates, as
      // ONE separator: "$7x+3=45$ \n \n Which..." is a blank line, but split
      // per newline it counted as max(1, 1) = one break (lost after equations).
      const first = m[0].indexOf('\n');
      if (first !== -1) emit(m.index + first, m.index + m[0].lastIndexOf('\n') + 1);
    }
  }
  parts.push(s.slice(last));
  return parts;
}

function splitRawIntoChunks(rawText) {
  if (!rawText) return [];

  let text = rawText;

  // Merge adjacent $\underline{\text{...}}$ segments separated by any spacer
  // ($ $, $$ $$, $\newline$, whitespace, etc.) into one continuous underline block.
  text = text.replace(
    /\$\\underline\{\\text\{([^}]+)\}\}\$(\s*(?:\$\$?\s*\$\$?|\$\$?\\\s*\$\$?|\$\$?\\newline\$\$?)\s*|\s+)(?=\$\\underline\{\\text\{)/g,
    '$\\underline{\\text{$1}}$'
  );
  // Now collapse adjacent $\underline{\text{A}}$$\underline{\text{B}}$ into one
  while (/\$\\underline\{\\text\{[^}]+\}\}\$\$\\underline\{\\text\{[^}]+\}\}\$/.test(text)) {
    text = text.replace(
      /\$\\underline\{\\text\{([^}]+)\}\}\$\$\\underline\{\\text\{([^}]+)\}\}\$/g,
      (_, a, b) => `$\\underline{\\text{${a}${b}}}$`
    );
  }

  // Strip $\underline{\text{...}}$ to bare text BEFORE splitting, matching
  // cleanForSplit in buildTTSSegments. This exposes sentence-ending punctuation
  // inside underlined text so splitting produces the same segment count.
  // We track the content so we can re-wrap after splitting for underline rendering.
  const underlineContents = [];
  text = text.replace(
    /\$\\underline\{\\text\{([^}]+)\}\}\$/g,
    (_, content) => { underlineContents.push(content); return content; }
  );
  // HTML underline tags: parts of the reading bank (function-of-a-sentence)
  // author the target sentence as literal <u>...</u>, which nothing rendered -
  // the tags printed as text. Route them through the same strip-track-rewrap
  // pipeline as $\underline{\text{...}}$.
  text = text.replace(/<u>\s*([\s\S]*?)\s*<\/u>/g, (_, content) => {
    underlineContents.push(content);
    return content;
  });

  // Clean structural LaTeX (same order as cleanForSplit in buildTTSSegments)
  // Amounts may write the thousands comma as {,} ($\$7{,}500$); missing that
  // left the $ unmatched and garbled the sentence (diagnostic-4 Math 2e #2).
  text = text.replace(/\$\$\s*\\\$(\d(?:[\d,]|\{,\})*\.?\d*)\s*\$\$/g, (_, a) => `__CURR_${a.replace(/\{,\}/g, ',')}__`);
  text = text.replace(/\$\\\$(\d(?:[\d,]|\{,\})*\.?\d*)\$/g, (_, a) => `__CURR_${a.replace(/\{,\}/g, ',')}__`);

  // Bold/italic headers: convert display math to inline math (preserves bold via <Latex>)
  // Collapse newlines/spacers before AND after them to a single \n each (no big gaps)
  text = text.replace(/(?:\s*\$\$?\\newline\$\$?\s*|\s*\$\$\\\s*\$\$\s*|\s*\$\$\s+\$\$\s*)*\$\$\\textbf\{([^}]*)\}\$\$\s*(?:\$\$?\\newline\$\$?\s*)*/g, (m, content, offset) => `${offset === 0 ? '' : '\n'}$\\textbf{${content}}$\n`);
  text = text.replace(/(?:\s*\$\$?\\newline\$\$?\s*|\s*\$\$\\\s*\$\$\s*|\s*\$\$\s+\$\$\s*)*\$\$\\textit\{([^}]*)\}\$\$\s*(?:\$\$?\\newline\$\$?\s*)*/g, (m, content, offset) => `${offset === 0 ? '' : '\n'}$\\textit{${content}}$\n`);

  // Paragraph break pattern: $$\newline$$ $$\ $$ $$\newline$$ → placeholder (protected from collapse)
  // Must run BEFORE individual newline/spacer conversions
  text = text.replace(/\$\$\\newline\$\$\s*\$\$\\\s*\$\$\s*\$\$\\newline\$\$/g, '__PARA_BREAK__');
  text = text.replace(/\$\$\\newline\$\$/g, '\n');
  text = text.replace(/\$\\newline\$/g, '\n');
  text = text.replace(/\$\$\\\s*\$\$/g, '\n');
  text = text.replace(/\$\$ +\$\$/g, '\n');
  // Collapse all multi-newline runs to single (literal \n + $$\newline$$ combos)
  text = text.replace(/\n{2,}/g, '\n');
  // Restore paragraph breaks as double newline
  text = text.replace(/__PARA_BREAK__/g, '\n\n');
  // Cross-text pairs: the XT bank authors ONE continuous string - "Text 1 ...
  // Text 2 ..." with no break markers at all - so the two passages ran
  // together as a single paragraph. Bluebook shows two labeled blocks: bold
  // each label onto its own line and open a paragraph gap before Text 2.
  // Placed after the newline collapse above so the inserted blank line survives.
  if (/^\s*Text 1\s/.test(text) && /[.!?"'”)\]]\s+Text 2\s/.test(text)) {
    text = text.replace(/^\s*Text 1\s+/, '$\\textbf{Text 1}$\n');
    text = text.replace(/([.!?"'”)\]])\s+Text 2\s+/, '$1\n\n$\\textbf{Text 2}$\n');
  }
  text = text.replace(/\$\\qquad\$\$\\bullet\$/g, '\n• ');
  text = text.replace(/\$\\bullet\$/g, '\n• ');
  text = text.replace(/\$\\qquad\$/g, ' ');
  // Don't strip underline/hspace/textbf here — we want the LaTeX preserved for rendering
  // But we DO need to split on the same boundaries
  text = text.replace(/\$\$\\underline\{\\hspace\{([^}]*)\}\}\$\$/g, (_, size) => ` __BLANK_${size}__ `);
  text = text.replace(/\$\\underline\{\\hspace\{([^}]*)\}\}\$/g, (_, size) => ` __BLANK_${size}__ `);
  text = text.replace(/\$\s*\\\s*\$/g, ' ');

  // Protect $...$ from splitting
  const latexBlocks = [];
  let working = text;
  working = working.replace(/\$\$[^$]*\$\$/g, (m) => { latexBlocks.push(m); return `__LB_${latexBlocks.length - 1}__`; });
  working = working.replace(/\$[^$]+\$/g, (m) => { latexBlocks.push(m); return `__LB_${latexBlocks.length - 1}__`; });

  // Protect abbreviations from being treated as sentence boundaries
  const ABBREVIATIONS = /\b(Dr|Mr|Mrs|Ms|Jr|Sr|St|Prof|Gen|Gov|Sgt|Lt|Col|Capt|Rev|Vol|vs|etc|approx|dept|est|govt|avg|inc|corp|No)\.\s/gi;
  working = working.replace(ABBREVIATIONS, (m, abbr) => `${abbr}__ABBR__ `);

  // Split on sentence-ending punctuation OR newlines, but track which splits were newlines
  // so we can preserve visual line breaks in the rendered output.
  const parts = splitSentenceParts(working);

  const chunks = [];
  let pendingNewlines = 0;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    // Check if this part is a delimiter (separator from the split)
    if (i % 2 === 1) {
      // It's a separator — count newlines to preserve paragraph spacing
      const nlCount = (part.match(/\n/g) || []).length;
      if (nlCount > 0) pendingNewlines = Math.max(pendingNewlines, nlCount);
      continue;
    }
    let raw = part.trim();
    if (!raw) continue;

    // Restore abbreviations
    raw = raw.replace(/__ABBR__/g, '.');
    // Restore latex blocks
    raw = raw.replace(/__LB_(\d+)__/g, (_, idx) => latexBlocks[parseInt(idx)]);
    // Restore currency
    raw = raw.replace(/__CURR_([\d,]+\.?\d*)__/g, (_, a) => `$\\$${a.replace(/,/g, '{,}')}$`);
    // Restore blanks back to original LaTeX
    raw = raw.replace(/__BLANK_([^_]+)__/g, (_, size) => `$$\\underline{\\hspace{${size}}}$$`);

    if (raw.trim()) {
      // Cap at 2 to avoid excessive spacing; 1 = new line, 2 = paragraph break
      chunks.push({ text: raw, breakCount: Math.min(pendingNewlines, 2) });
      pendingNewlines = 0;
    }
  }

  // Re-wrap underlined content so processText can render with underline styling.
  // Each underline content should appear in exactly one chunk.
  for (const ulContent of underlineContents) {
    for (const chunk of chunks) {
      if (chunk.text.includes(ulContent)) {
        chunk.text = chunk.text.replace(
          ulContent,
          `$\\underline{\\text{${ulContent}}}$`
        );
        break;
      }
    }
  }

  return chunks;
}

export default LatexRenderer;